/**
 * @fileoverview Import/Export utilities for master equipment settings
 * @description Handles serialization and file operations for settings
 */

import type {
  SurveyMasterEquipmentSettings,
  MasterEquipmentSettingsFileData,
} from '@/types/master-equipment.types'

/**
 * Export settings to JSON file data structure
 */
export function exportSettingsToFile(
  settings: SurveyMasterEquipmentSettings
): MasterEquipmentSettingsFileData {
  return {
    fileVersion: '1.0',
    exportedAt: new Date().toISOString(),
    settings,
  }
}

/**
 * Import settings from JSON file data
 *
 * @returns Updated settings or null if invalid
 */
export function importSettingsFromFile(
  data: MasterEquipmentSettingsFileData
): SurveyMasterEquipmentSettings | null {
  try {
    const { settings: importedSettings } = data

    // Validate the data
    if (!importedSettings.surveyId || !importedSettings.equipmentConfigs) {
      return null
    }

    // Return updated settings with new timestamp
    return {
      ...importedSettings,
      updatedAt: new Date().toISOString(),
    }
  } catch {
    return null
  }
}

/**
 * Download settings as JSON file to user's device
 */
export function downloadSettingsAsFile(data: MasterEquipmentSettingsFileData): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `master-equipment-settings-${data.settings.surveyId}-${Date.now()}.json`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
